import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { ORG_ADMIN_DASHBOARD_QUERY_KEY } from "@/hooks/queries/useOrgAdminDashboard";

// The four writes the Edit-member modal hosts (Stories 14.4 + 14.5 + 14.6,
// FR75/FR56e/FR83). They share one file because they share everything that
// matters: the same `/org-admin/clinicians/{user_id}/{action}` shape, the same
// actor, and the same success consequence — the dashboard roster the modal was
// opened from is now stale, so each invalidates it.
//
// Mirrors useCreateOrgInvitation: `skipAuthRefresh` is OMITTED (post-session data
// calls ride the silent-refresh path), and every response is PHI-free.

/** Wire body of POST …/change-email. */
export interface ChangeClinicianEmailRequest {
  new_email: string;
}

/** Envelope-unwrapped 200 payload of …/change-email. */
export interface ChangeClinicianEmailData {
  target_user_id: string;
  new_email: string;
  user_origin: string;
}

/** Wire body of POST …/update-details (all three FR75 metadata fields, always). */
export interface UpdateClinicianDetailsRequest {
  organization_role_label: string;
  department: string | null;
  hr_id: string | null;
}

/** Envelope-unwrapped 200 payload of …/update-details — what actually persisted. */
export interface UpdateClinicianDetailsData {
  target_user_id: string;
  organization_role_label: string;
  department: string | null;
  hr_id: string | null;
}

/** Envelope-unwrapped 200 payload of …/reset-password. Carries NO credential. */
export interface ResetClinicianPasswordData {
  target_user_id: string;
  emailed_to: string;
}

/**
 * Envelope-unwrapped 200 payload of …/remove-seat (Story 14.6). The server
 * states the FR83 invariant on the wire rather than leaving the client to infer
 * it: `seat_consumed_unchanged` is always true, and removal never frees a slot.
 */
export interface RemoveClinicianFromSeatData {
  target_user_id: string;
  removed_at: string;
  seat_consumed_unchanged: boolean;
}

function memberActionPath(userId: string, action: string): string {
  return `/org-admin/clinicians/${encodeURIComponent(userId)}/${action}`;
}

export function useChangeClinicianEmail(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ChangeClinicianEmailRequest) =>
      apiFetch<ChangeClinicianEmailData>(memberActionPath(userId, "change-email"), {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ORG_ADMIN_DASHBOARD_QUERY_KEY });
    },
  });
}

export function useUpdateClinicianDetails(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateClinicianDetailsRequest) =>
      apiFetch<UpdateClinicianDetailsData>(
        memberActionPath(userId, "update-details"),
        { method: "POST", body },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ORG_ADMIN_DASHBOARD_QUERY_KEY });
    },
  });
}

export function useResetClinicianPassword(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // No body: the target is the path, and the temp password is minted server-side
    // and never round-trips through this client.
    mutationFn: () =>
      apiFetch<ResetClinicianPasswordData>(
        memberActionPath(userId, "reset-password"),
        { method: "POST" },
      ),
    onSuccess: () => {
      // The reset flips nothing the roster renders today, but it DOES burn the
      // target's sessions; refetching keeps this modal reading from one server
      // truth rather than a snapshot taken before the write.
      void queryClient.invalidateQueries({ queryKey: ORG_ADMIN_DASHBOARD_QUERY_KEY });
    },
  });
}

export function useRemoveClinicianFromSeat(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // No body — the target is the path, and there is nothing to parameterise:
    // FR83 fixes the outcome (access ends, the seat stays consumed).
    mutationFn: () =>
      apiFetch<RemoveClinicianFromSeatData>(
        memberActionPath(userId, "remove-seat"),
        { method: "POST" },
      ),
    onSuccess: () => {
      // The row does NOT disappear — it transitions to the Removed state, so the
      // dead slot stays countable against the unchanged Consumed tile. The
      // refetch is what carries that transition, and it is also what drops the
      // row out of Pending invites when the removed member was still invited.
      void queryClient.invalidateQueries({ queryKey: ORG_ADMIN_DASHBOARD_QUERY_KEY });
    },
  });
}
